import { ScheduleMatch } from '../services/scheduleService';

export interface UnverifiedDelta {
  played: number;
  won: number;
  drawn: number;
  lost: number;
}

/**
 * Compute how many unverified match results affect a given team's record.
 * Returns null if there are no unverified matches for the team.
 */
export function computeUnverifiedDelta(
  matches: ScheduleMatch[],
  teamName: string
): UnverifiedDelta | null {
  const unverified = matches.filter(
    m =>
      m.scoreVerified === false &&
      m.status === 'COMPLETED' &&
      m.homeScore !== undefined &&
      m.awayScore !== undefined &&
      (m.homeTeam === teamName || m.awayTeam === teamName)
  );

  if (unverified.length === 0) return null;

  let won = 0;
  let drawn = 0;
  let lost = 0;

  for (const m of unverified) {
    const isHome = m.homeTeam === teamName;
    const teamScore = isHome ? m.homeScore! : m.awayScore!;
    const opponentScore = isHome ? m.awayScore! : m.homeScore!;

    if (teamScore > opponentScore) won++;
    else if (teamScore === opponentScore) drawn++;
    else lost++;
  }

  return { played: unverified.length, won, drawn, lost };
}

/**
 * Build a map of team name -> unverified delta for all teams that have
 * unverified matches in the provided match list.
 */
export function computeAllUnverifiedDeltas(
  matches: ScheduleMatch[]
): Record<string, UnverifiedDelta> {
  const unverifiedMatches = matches.filter(
    m =>
      m.scoreVerified === false &&
      m.status === 'COMPLETED' &&
      m.homeScore !== undefined &&
      m.awayScore !== undefined
  );

  if (unverifiedMatches.length === 0) return {};

  const deltas: Record<string, UnverifiedDelta> = {};

  for (const m of unverifiedMatches) {
    for (const teamName of [m.homeTeam, m.awayTeam]) {
      if (!deltas[teamName]) {
        deltas[teamName] = { played: 0, won: 0, drawn: 0, lost: 0 };
      }

      const isHome = teamName === m.homeTeam;
      const teamScore = isHome ? m.homeScore! : m.awayScore!;
      const opponentScore = isHome ? m.awayScore! : m.homeScore!;

      deltas[teamName].played++;
      if (teamScore > opponentScore) deltas[teamName].won++;
      else if (teamScore === opponentScore) deltas[teamName].drawn++;
      else deltas[teamName].lost++;
    }
  }

  return deltas;
}
